/**
 * Freedom Product Page JavaScript
 * Handles Swiper initialization, thumbnail switching, variant selection, cart updates, and wishlist
 * DIAGNOSTIC VERSION - logs all cart operations
 */

(function() {
  'use strict';

  // Only run on Freedom product pages
  if (!document.querySelector('.freedom-product')) {
    return;
  }

  let productSwiper = null;
  let productData = null;

  // DIAGNOSTIC: Log function
  function logDiagnostic(message, data) {
    console.log('[FREEDOM-CART-DIAG]', message, data || '');
  }

  function init() {
    // Get product data from Shopify
    if (typeof window.Shopify !== 'undefined' && window.Shopify.routes) {
      fetchProductData();
    }
    
    initSwiper();
    initThumbnails();
    initVariantSelector();
    initVariantValidation();
    initCartCount();
    initBuyItNow();
    initWishlist();
    loadWishlistState();
    
    logDiagnostic('Freedom product page initialized');
    
    // Initial cart count update on page load
    updateHeaderCartCount();
  }

  // Fetch product variant data
  function fetchProductData() {
    const productForm = document.getElementById('freedom-product-form');
    if (!productForm) return;

    // Get product handle from URL or form
    const urlParts = window.location.pathname.split('/');
    const productHandle = urlParts[urlParts.length - 1];
    
    fetch('/products/' + productHandle + '.js')
      .then(response => response.json())
      .then(product => {
        productData = product;
        checkVariantSelection();
      })
      .catch(() => {
        // Silently fail, will use form-based validation
      });
  }

  // Initialize Swiper for product images
  function initSwiper() {
    if (typeof Swiper === 'undefined') {
      setTimeout(function() {
        if (typeof Swiper !== 'undefined') {
          initSwiper();
        }
      }, 100);
      return;
    }

    const swiperContainer = document.querySelector('.freedom-product [data-product-swiper]');
    if (!swiperContainer) return;

    const slides = swiperContainer.querySelectorAll('.swiper-slide');
    if (slides.length < 2) return;

    const scrollbarWrap = document.querySelector('.freedom-product__scrollbar-wrap');
    if (!scrollbarWrap) return;

    const scrollbarEl = scrollbarWrap.querySelector('.swiper-scrollbar');
    if (!scrollbarEl) return;

    productSwiper = new Swiper(swiperContainer, {
      slidesPerView: 1,
      loop: false,
      speed: 250,
      allowTouchMove: true,
      navigation: false,
      scrollbar: {
        el: scrollbarEl,
        draggable: true,
        hide: false
      }
    });

    // Update Swiper after images load
    requestAnimationFrame(() => {
      if (productSwiper) productSwiper.update();
    });

    const images = swiperContainer.querySelectorAll('img');
    let imagesToLoad = images.length;

    if (imagesToLoad === 0) {
      if (productSwiper) productSwiper.update();
    } else {
      images.forEach(img => {
        if (img.complete) {
          imagesToLoad--;
        } else {
          img.addEventListener('load', () => {
            imagesToLoad--;
            if (imagesToLoad === 0 && productSwiper) productSwiper.update();
          });
          img.addEventListener('error', () => {
            imagesToLoad--;
            if (imagesToLoad === 0 && productSwiper) productSwiper.update();
          });
        }
      });
      if (imagesToLoad === 0 && productSwiper) productSwiper.update();
    }
  }

  // Initialize thumbnail switching
  function initThumbnails() {
    const thumbnails = document.querySelectorAll('.freedom-product__thumbnail');
    if (thumbnails.length === 0) return;

    thumbnails.forEach(function(thumb, index) {
      thumb.addEventListener('click', function() {
        // Update active state
        thumbnails.forEach(function(t) {
          t.classList.remove('freedom-product__thumbnail--active');
        });
        this.classList.add('freedom-product__thumbnail--active');

        // Switch Swiper slide if Swiper exists
        if (productSwiper) {
          productSwiper.slideTo(index);
        }
      });
    });
  }

  // Initialize variant selector to update hidden variant select
  function initVariantSelector() {
    const variantSelects = document.querySelectorAll('.freedom-product__variant-select');
    const hiddenVariantSelect = document.getElementById('freedom-variant-select');
    if (!hiddenVariantSelect || variantSelects.length === 0) return;

    variantSelects.forEach(function(select) {
      select.addEventListener('change', function() {
        updateVariantSelection();
        checkVariantSelection();
        updatePillActiveState(select);
      });
    });

    // Also listen for changes on the hidden select (if changed programmatically)
    hiddenVariantSelect.addEventListener('change', function() {
      checkVariantSelection();
    });

    // Pill buttons: click updates hidden select and active state
    function updatePillActiveState(select) {
      const variantBlock = select.closest('.freedom-product__variant');
      if (!variantBlock) return;
      const pills = variantBlock.querySelectorAll('.freedom-product__variant-pill');
      const value = select.value || '';
      pills.forEach(function(pill) {
        const isActive = pill.getAttribute('data-value') === value;
        pill.classList.toggle('freedom-product__variant-pill--active', isActive);
        pill.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });
    }
    document.querySelectorAll('.freedom-product__variant-pill').forEach(function(pill) {
      pill.addEventListener('click', function() {
        const variantBlock = this.closest('.freedom-product__variant');
        const select = variantBlock && variantBlock.querySelector('.freedom-product__variant-select');
        if (!select) return;
        const value = this.getAttribute('data-value') || '';
        select.value = value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
        updatePillActiveState(select);
      });
    });
    variantSelects.forEach(updatePillActiveState);
  }

  // HARD-FIX: Prevent form submission completely
  function initFormPrevention() {
    const form = document.getElementById('freedom-product-form') || document.querySelector('form[action*="/cart/add"]');
    if (!form) return;

    logDiagnostic('Form found, attaching prevention handlers', {
      formId: form.id,
      formAction: form.action || 'none',
      formMethod: form.method || 'GET'
    });

    // Prevent ALL form submissions
    form.addEventListener('submit', function(e) {
      logDiagnostic('Form submit event intercepted', {
        defaultPrevented: e.defaultPrevented,
        target: e.target.tagName
      });
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      return false;
    }, true); // Use capture phase

    // Also prevent on form element directly
    form.onsubmit = function(e) {
      logDiagnostic('Form onsubmit handler triggered');
      e.preventDefault();
      e.stopPropagation();
      return false;
    };
  }

  // Initialize variant validation and add-to-cart
  function initVariantValidation() {
    // First, prevent form submission
    initFormPrevention();

    const addToBagBtn = document.getElementById('freedom-add-to-bag') || document.querySelector('[data-freedom-atc]');
    const productForm = document.getElementById('freedom-product-form') || document.querySelector('form[action*="/cart/add"]');
    
    if (!addToBagBtn) {
      logDiagnostic('ERROR: Add to bag button not found');
      return;
    }

    if (!productForm) {
      logDiagnostic('ERROR: Product form not found');
      return;
    }

    logDiagnostic('Add to bag button found', {
      buttonType: addToBagBtn.type,
      buttonId: addToBagBtn.id,
      hasHref: addToBagBtn.href || false,
      formAction: productForm.action || 'none',
      formMethod: productForm.method || 'GET'
    });

    // Check initial state
    checkVariantSelection();

    // HARD-FIX: Attach click handler directly to button (not form submit)
    addToBagBtn.addEventListener('click', function(e) {
      logDiagnostic('Add to bag button clicked', {
        defaultPrevented: e.defaultPrevented,
        buttonType: this.type,
        buttonHref: this.href || 'none'
      });

      // ALWAYS prevent default and stop propagation
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      // Validate variant selection
      if (!isVariantSelected()) {
        logDiagnostic('Variant validation failed - showing error');
        showVariantError('Please choose the variants first.');
        return false;
      }

      // Get variant ID from hidden select
      const hiddenVariantSelect = document.getElementById('freedom-variant-select');
      if (!hiddenVariantSelect || !hiddenVariantSelect.value) {
        logDiagnostic('Variant ID not found');
        showVariantError('Please choose the variants first.');
        return false;
      }

      const variantId = hiddenVariantSelect.value;
      const quantity = 1;

      logDiagnostic('Preparing to add to cart', {
        variantId: variantId,
        quantity: quantity,
        fetchUrl: '/cart/add.js'
      });

      // Disable button during request
      addToBagBtn.disabled = true;
      const originalText = addToBagBtn.textContent;
      addToBagBtn.textContent = 'ADDING...';

      // Add to cart via AJAX - DIAGNOSTIC: Log the exact fetch call
      const fetchUrl = '/cart/add.js';
      logDiagnostic('Fetching cart add endpoint', {
        url: fetchUrl,
        method: 'POST',
        body: JSON.stringify({ id: variantId, quantity: quantity })
      });

      fetch(fetchUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          id: variantId,
          quantity: quantity
        })
      })
        .then(response => {
          logDiagnostic('Cart add response received', {
            status: response.status,
            statusText: response.statusText,
            ok: response.ok,
            url: response.url
          });

          if (!response.ok) {
            return response.json().then(err => {
              logDiagnostic('Cart add error response', err);
              return Promise.reject(err);
            });
          }
          return response.json();
        })
        .then(item => {
          logDiagnostic('Cart add success', item);
          
          // Success - update cart count and open drawer
          // Dispatch event to trigger updates
          document.dispatchEvent(new CustomEvent('cart:updated', {
            bubbles: true,
            detail: { cart: item }
          }));
          openCartDrawer();
          
          // Re-enable button
          addToBagBtn.disabled = false;
          addToBagBtn.textContent = originalText;
          hideVariantError();
        })
        .catch(error => {
          logDiagnostic('Cart add fetch error', error);
          
          // Show error message
          const errorMsg = error.description || error.message || 'Unable to add product to cart. Please try again.';
          showVariantError(errorMsg);
          
          // Re-enable button
          addToBagBtn.disabled = false;
          addToBagBtn.textContent = originalText;
        });
      
      return false;
    }, true); // Use capture phase to catch early

    // ALSO prevent form submission as backup
    productForm.addEventListener('submit', function(e) {
      logDiagnostic('Form submit event (backup handler)', {
        defaultPrevented: e.defaultPrevented
      });
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      return false;
    }, true);
  }

  // Initialize Buy It Now button
  function initBuyItNow() {
    const buyNowBtn = document.getElementById('freedom-buy-now');
    if (!buyNowBtn) return;

    buyNowBtn.addEventListener('click', function(e) {
      e.preventDefault();
      e.stopPropagation();

      if (!isVariantSelected()) {
        showVariantError('Please choose the variants first.');
        return;
      }

      const hiddenVariantSelect = document.getElementById('freedom-variant-select');
      if (!hiddenVariantSelect || !hiddenVariantSelect.value) {
        showVariantError('Please choose the variants first.');
        return;
      }

      const variantId = hiddenVariantSelect.value;
      const quantity = 1;

      // Disable button during request
      buyNowBtn.disabled = true;
      buyNowBtn.textContent = 'ADDING...';

      // Add to cart then redirect to checkout
      fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          id: variantId,
          quantity: quantity
        })
      })
        .then(response => {
          if (!response.ok) {
            return response.json().then(err => Promise.reject(err));
          }
          return response.json();
        })
        .then(() => {
          // Redirect to checkout
          window.location.href = '/checkout';
        })
        .catch(error => {
          console.error('Error adding to cart:', error);
          const errorMsg = error.description || error.message || 'Unable to add product to cart. Please try again.';
          showVariantError(errorMsg);
          
          // Re-enable button
          buyNowBtn.disabled = false;
          buyNowBtn.textContent = 'BUY IT NOW';
        });
    });
  }

  // Check if variant is selected
  function isVariantSelected() {
    const variantSelects = document.querySelectorAll('.freedom-product__variant-select');
    if (variantSelects.length === 0) return true; // No variants required

    const hiddenVariantSelect = document.getElementById('freedom-variant-select');
    if (!hiddenVariantSelect) return false;

    // Check if all option selects have values
    let allSelected = true;
    variantSelects.forEach(function(select) {
      if (!select.value || select.value === '' || select.value.includes('[Required]')) {
        allSelected = false;
      }
    });

    // Check if hidden variant select has a valid variant ID
    const variantId = hiddenVariantSelect.value;
    if (!variantId || variantId === '') {
      return false;
    }

    return allSelected;
  }

  // Check variant selection and update button states
  function checkVariantSelection() {
    const addToBagBtn = document.getElementById('freedom-add-to-bag');
    const buyNowBtn = document.getElementById('freedom-buy-now');
    const variantError = document.getElementById('freedom-variant-error');
    
    const hasVariants = document.querySelectorAll('.freedom-product__variant-select').length > 0;
    const variantSelected = isVariantSelected();
    const hiddenVariantSelect = document.getElementById('freedom-variant-select');
    const variantId = hiddenVariantSelect ? hiddenVariantSelect.value : null;
    
    // Check if variant is available
    let variantAvailable = true;
    if (productData && variantId) {
      const variant = productData.variants.find(v => v.id.toString() === variantId.toString());
      if (variant) {
        variantAvailable = variant.available;
      }
    }

    if (hasVariants) {
      if (variantSelected && variantAvailable) {
        if (addToBagBtn) {
          addToBagBtn.disabled = false;
          addToBagBtn.classList.remove('freedom-product__button--disabled');
        }
        if (buyNowBtn) {
          buyNowBtn.disabled = false;
          buyNowBtn.classList.remove('freedom-product__button--disabled');
        }
        if (variantError) {
          variantError.style.display = 'none';
        }
      } else {
        if (addToBagBtn) {
          addToBagBtn.disabled = true;
          addToBagBtn.classList.add('freedom-product__button--disabled');
        }
        if (buyNowBtn) {
          buyNowBtn.disabled = true;
          buyNowBtn.classList.add('freedom-product__button--disabled');
        }
      }
    } else {
      // No variants, buttons should be enabled if product is available
      if (addToBagBtn) {
        addToBagBtn.disabled = !variantAvailable;
      }
      if (buyNowBtn) {
        buyNowBtn.disabled = !variantAvailable;
      }
    }
  }

  // Show variant error message
  function showVariantError(message) {
    const variantError = document.getElementById('freedom-variant-error');
    if (variantError) {
      variantError.textContent = message || 'Please choose the variants first.';
      variantError.style.display = 'block';
      // Scroll to error
      variantError.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }

  // Hide variant error message
  function hideVariantError() {
    const variantError = document.getElementById('freedom-variant-error');
    if (variantError) {
      variantError.style.display = 'none';
    }
  }

  // Update variant selection based on option selects (supports hidden options e.g. Color)
  function updateVariantSelection() {
    const variantSelects = document.querySelectorAll('.freedom-product__variant-select');
    const hiddenVariantSelect = document.getElementById('freedom-variant-select');
    if (!hiddenVariantSelect) return;

    // Build selected options by index (e.g. [ '', 'XL' ] when only Size is shown)
    const selectedOptions = [ '', '', '' ];
    variantSelects.forEach(function(select) {
      const idx = parseInt(select.getAttribute('data-option-index'), 10);
      if (!isNaN(idx) && idx >= 0 && idx <= 2) {
        selectedOptions[idx] = select.value || '';
      }
    });

    const variantOptions = hiddenVariantSelect.querySelectorAll('option');
    const variantValues = [ 'data-option1', 'data-option2', 'data-option3' ];
    for (let i = 0; i < variantOptions.length; i++) {
      const option = variantOptions[i];
      const opt1 = option.getAttribute('data-option1') || '';
      const opt2 = option.getAttribute('data-option2') || '';
      const opt3 = option.getAttribute('data-option3') || '';
      const variantOpts = [ opt1, opt2, opt3 ];

      let match = true;
      let hasSelection = false;
      for (let j = 0; j <= 2; j++) {
        const sel = selectedOptions[j];
        if (sel) hasSelection = true;
        if (sel) {
          const vOpt = (variantOpts[j] || '').toLowerCase();
          if (vOpt !== sel.toLowerCase()) {
            match = false;
            break;
          }
        }
      }
      if (match && hasSelection) {
        hiddenVariantSelect.value = option.value;
        hiddenVariantSelect.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }

  // Update cart count display on product page (TOTAL line)
  function updateProductPageCartCount() {
    const cartCountEl = document.querySelector('.freedom-product [data-cart-count]');
    if (!cartCountEl) return;

    fetch('/cart.js')
      .then(response => response.json())
      .then(cart => {
        const count = cart.item_count || 0;
        cartCountEl.textContent = count + ' (' + count + ' item' + (count !== 1 ? 's' : '') + ')';
      })
      .catch(() => {
        // Silently fail
      });
  }

  // Update Freedom cart count in header (uses global function if available)
  function updateHeaderCartCount() {
    logDiagnostic('Updating header cart count');
    
    // Use global function if available (from freedom-cart-count.js)
    if (typeof window.updateFreedomCartCount === 'function') {
      fetch('/cart.js')
        .then(response => response.json())
        .then(cart => {
          const count = cart.item_count || 0;
          logDiagnostic('Cart data received', {
            item_count: count
          });
          window.updateFreedomCartCount(count);
        })
        .catch(error => {
          logDiagnostic('Error fetching cart count', error);
        });
    } else {
      // Fallback: direct update
      fetch('/cart.js')
        .then(response => response.json())
        .then(cart => {
          const count = cart.item_count || 0;
          const targets = document.querySelectorAll('[data-freedom-cart-count]');
          targets.forEach(el => {
            if (count > 0) {
              el.textContent = String(count);
              el.style.display = 'inline-block';
            } else {
              el.style.display = 'none';
            }
          });
        })
        .catch(() => {});
    }
  }

  // FIXED: Open cart drawer without redirect fallback
  function openCartDrawer() {
    logDiagnostic('Attempting to open cart drawer');
    
    const cartDrawer = document.getElementById('cart-drawer') || document.querySelector('cart-drawer');
    
    if (cartDrawer) {
      logDiagnostic('Cart drawer element found', {
        hasRenderContents: typeof cartDrawer.renderContents === 'function',
        hasOpen: typeof cartDrawer.open === 'function'
      });
      
      // Try to use theme's cart drawer
      if (typeof cartDrawer.renderContents === 'function') {
        // Fetch cart drawer HTML
        fetch('/?section_id=cart-drawer')
          .then(response => {
            logDiagnostic('Cart drawer section fetch response', {
              status: response.status,
              ok: response.ok
            });
            return response.text();
          })
          .then(html => {
            const parser = new DOMParser();
            const doc = parser.parseFromString(html, 'text/html');
            const cartDrawerHTML = doc.querySelector('cart-drawer') || doc.querySelector('#cart-drawer');
            
            if (cartDrawerHTML) {
              // Parse sections
              const sections = {
                'cart-drawer': cartDrawerHTML.querySelector('.cart-drawer')?.innerHTML || '',
                'cart-icon-bubble': cartDrawerHTML.querySelector('#cart-icon-bubble')?.innerHTML || ''
              };
              
              logDiagnostic('Rendering cart drawer contents');
              cartDrawer.renderContents({ sections: sections });
            } else {
              logDiagnostic('Cart drawer HTML not found in response');
              // Fallback: just open the drawer
              if (typeof cartDrawer.open === 'function') {
                cartDrawer.open();
              }
            }
          })
          .catch(error => {
            logDiagnostic('Error fetching cart drawer section', error);
            // If fetch fails, just open the drawer
            if (typeof cartDrawer.open === 'function') {
              cartDrawer.open();
            }
          });
      } else if (typeof cartDrawer.open === 'function') {
        logDiagnostic('Opening cart drawer directly');
        cartDrawer.open();
      } else {
        logDiagnostic('Cart drawer found but no open/render methods available');
      }
    } else {
      logDiagnostic('Cart drawer element not found - NO REDIRECT (this is intentional)');
      // REMOVED: window.location.href = '/cart';
      // Instead, just log that drawer is not available
      // User can click cart icon to go to /cart page
    }
  }

  // Initialize cart count display
  function initCartCount() {
    // Listen for cart updates
    document.addEventListener('cart:updated', function() {
      updateHeaderCartCount(); // This will fetch /cart.js and call updateCartCount()
      updateProductPageCartCount(); // Update product page TOTAL line
    });

    // Initial update on page load
    updateHeaderCartCount();
    updateProductPageCartCount();
  }

  // Initialize wishlist functionality
  function initWishlist() {
    const wishlistBtn = document.getElementById('freedom-wishlist-btn') || document.querySelector('[data-wishlist]');
    if (!wishlistBtn) return;

    wishlistBtn.addEventListener('click', function(e) {
      e.preventDefault();
      
      const productHandle = this.getAttribute('data-product-handle');
      if (!productHandle) return;

      // Check if customer is logged in
      const isLoggedIn = window.FREEDOM_CUSTOMER_LOGGED_IN === true || 
                         document.body.classList.contains('customer-logged-in') ||
                         document.querySelector('[data-customer-logged-in]');

      if (!isLoggedIn) {
        // Redirect to login with return URL
        const currentUrl = encodeURIComponent(window.location.href);
        window.location.href = '/account/login?return_url=' + currentUrl;
        return;
      }

      // Customer is logged in - toggle wishlist
      toggleWishlist(productHandle, wishlistBtn);
    });
  }

  // Toggle wishlist item
  function toggleWishlist(productHandle, button) {
    let wishlist = getWishlist();
    const index = wishlist.indexOf(productHandle);
    
    if (index > -1) {
      // Remove from wishlist
      wishlist.splice(index, 1);
      button.classList.remove('freedom-product__wishlist--active');
      button.querySelector('svg path').setAttribute('fill', 'none');
      showToast('Removed from wishlist');
    } else {
      // Add to wishlist
      wishlist.push(productHandle);
      button.classList.add('freedom-product__wishlist--active');
      button.querySelector('svg path').setAttribute('fill', 'currentColor');
      showToast('Saved for later');
    }
    
    saveWishlist(wishlist);
  }

  // Get wishlist from localStorage
  function getWishlist() {
    try {
      const stored = localStorage.getItem('freedom_wishlist');
      return stored ? JSON.parse(stored) : [];
    } catch (e) {
      return [];
    }
  }

  // Save wishlist to localStorage
  function saveWishlist(wishlist) {
    try {
      localStorage.setItem('freedom_wishlist', JSON.stringify(wishlist));
    } catch (e) {
      console.error('Error saving wishlist:', e);
    }
  }

  // Load wishlist state on page load
  function loadWishlistState() {
    const wishlistBtn = document.getElementById('freedom-wishlist-btn') || document.querySelector('[data-wishlist]');
    if (!wishlistBtn) return;

    const productHandle = wishlistBtn.getAttribute('data-product-handle');
    if (!productHandle) return;

    const wishlist = getWishlist();
    if (wishlist.indexOf(productHandle) > -1) {
      wishlistBtn.classList.add('freedom-product__wishlist--active');
      const path = wishlistBtn.querySelector('svg path');
      if (path) {
        path.setAttribute('fill', 'currentColor');
      }
    }
  }

  // Show toast notification
  function showToast(message) {
    // Remove existing toast if any
    const existingToast = document.querySelector('.freedom-toast');
    if (existingToast) {
      existingToast.remove();
    }

    // Create toast element
    const toast = document.createElement('div');
    toast.className = 'freedom-toast';
    toast.textContent = message;
    toast.style.cssText = 'position: fixed; bottom: 20px; left: 50%; transform: translateX(-50%); background: #000; color: #fff; padding: 12px 24px; border-radius: 4px; z-index: 10000; font-family: Pretendard, Arial, sans-serif; font-size: 14px;';
    document.body.appendChild(toast);

    // Remove after 3 seconds
    setTimeout(() => {
      toast.style.opacity = '0';
      toast.style.transition = 'opacity 0.3s';
      setTimeout(() => toast.remove(), 300);
    }, 3000);
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-initialize on section reload
  document.addEventListener('shopify:section:load', function(event) {
    if (event.detail.sectionId && document.querySelector('[data-section-id="' + event.detail.sectionId + '"]')) {
      if (productSwiper) {
        productSwiper.destroy(true, true);
        productSwiper = null;
      }
      init();
    }
  });
})();
